/**
 * Guided-setup persistence — load / save / subscribe for the org-level
 * KPI list and governance notes.
 *
 * Per the projection-layer contract (src/lib/brandBrain/persist.ts, whose
 * mechanism this mirrors exactly): NO new Store-interface methods. Each
 * document rides the shipped `db` store as ONE JSON-safe record inside the
 * existing `brainFacts` collection — a synthetic org-scoped fact
 * (`accountId: '__org__'`) with a deterministic id, so every save upserts
 * the same row. Firestore rules, the localStorage adapter, and the Store
 * interface all stay untouched.
 *
 * All (de)serialization is the PURE, total model in ./setupWalkthrough.ts;
 * this file is read → parse and serialize → put, nothing clever.
 */

import { db, nowIso } from './db/store';
import type { BrainFact } from './db/types';
import {
  GOVERNANCE_NOTES_FACT_ID,
  ORG_KPIS_FACT_ID,
  SETUP_ORG_ACCOUNT_ID,
  parseGovernanceNotes,
  parseOrgKpis,
  serializeGovernanceNotes,
  serializeOrgKpis,
  type GovernanceNotes,
  type OrgKpi,
} from './setupWalkthrough';

function toFact(
  id: string,
  section: 'goals' | 'rules',
  field: string,
  label: string,
  value: string,
  updatedBy: string,
  version: number,
): BrainFact {
  return {
    id,
    accountId: SETUP_ORG_ACCOUNT_ID,
    section,
    field,
    label,
    value,
    source: 'operator',
    confidence: 1,
    version,
    setBy: updatedBy || 'operator',
    setAt: nowIso(),
  };
}

// ---------------------------------------------------------------------------
// Org KPIs
// ---------------------------------------------------------------------------

/** One-shot read of the org KPI list; [] when unset/corrupt. */
export async function loadOrgKpis(): Promise<OrgKpi[]> {
  const fact = await db.get('brainFacts', ORG_KPIS_FACT_ID);
  return parseOrgKpis(fact?.value);
}

/** Upsert the whole org KPI list (bumps the fact's version). */
export async function saveOrgKpis(kpis: OrgKpi[], updatedBy = 'operator'): Promise<void> {
  const existing = await db.get('brainFacts', ORG_KPIS_FACT_ID);
  await db.put(
    'brainFacts',
    toFact(
      ORG_KPIS_FACT_ID,
      'goals',
      'orgKpis',
      'Org KPIs & targets',
      serializeOrgKpis(kpis),
      updatedBy,
      (existing?.version ?? 0) + 1,
    ),
  );
}

// ---------------------------------------------------------------------------
// Governance notes (SLA + process)
// ---------------------------------------------------------------------------

/** One-shot read of the governance notes; empty notes when unset/corrupt. */
export async function loadGovernanceNotes(): Promise<GovernanceNotes> {
  const fact = await db.get('brainFacts', GOVERNANCE_NOTES_FACT_ID);
  return parseGovernanceNotes(fact?.value);
}

/** Upsert the governance notes (bumps the fact's version). */
export async function saveGovernanceNotes(
  notes: GovernanceNotes,
  updatedBy = 'operator',
): Promise<void> {
  const existing = await db.get('brainFacts', GOVERNANCE_NOTES_FACT_ID);
  await db.put(
    'brainFacts',
    toFact(
      GOVERNANCE_NOTES_FACT_ID,
      'rules',
      'governanceNotes',
      'Governance — SLA & process notes',
      serializeGovernanceNotes(notes),
      updatedBy,
      (existing?.version ?? 0) + 1,
    ),
  );
}
